// /dashboard/:orgSlug/analytics/usage — the usage axis, with the two views the
// overview has no room for: the clock (X2) and the per-document ridgeline (X6).
//
// EVERY CONTROL LIVES IN THE URL, not in component state. A 90-day quarter-hour
// clock filtered to one term survives a paste into a new tab, and the browser
// back button returns to the view that was being looked at rather than to a
// reset one. `?days=` was already URL-owned; `cycle`, `grain` and `corpus`
// follow it for the same reason.
//
// `validateSearch` NARROWS rather than trusts: an unrecognised `cycle` or
// `grain` falls back to the default instead of reaching the API, so a
// hand-edited URL cannot produce a 422 the screen has no state for.

import { createRoute } from "@tanstack/react-router";
import { shellRoute } from "./_shell";
import { AnalyticsScreen } from "../screens/analytics/AnalyticsScreen";
import {
  DEFAULT_RANGE,
  isRangeDays,
  type RangeDays,
} from "../screens/analytics/RangeChips";
import type { ClockCycle, ClockGrain } from "../screens/analytics/api";

type UsageSearch = {
  days: RangeDays;
  cycle: ClockCycle;
  grain: ClockGrain;
  corpus?: string;
  term?: string;
};

export const analyticsUsageRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/dashboard/$orgSlug/analytics/usage",
  staticData: { title: "Analytics · Usage" },
  validateSearch: (search: Record<string, unknown>): UsageSearch => {
    const raw = Number(search.days);
    const cycle = search.cycle === "week" ? "week" : "day";
    // Note "minute" is deliberately NOT accepted: the control draws it and
    // refuses it, and the API has no rollup at that grain (see
    // `backend/analytics/schema.sql`'s clock_metrics comment).
    const grain = search.grain === "quarter" ? "quarter" : "hour";
    return {
      days: isRangeDays(raw) ? raw : DEFAULT_RANGE,
      cycle,
      grain,
      corpus: typeof search.corpus === "string" ? search.corpus : undefined,
      term: typeof search.term === "string" ? search.term : undefined,
    };
  },
  component: AnalyticsUsageRoute,
});

function AnalyticsUsageRoute() {
  const { orgSlug } = analyticsUsageRoute.useParams();
  const search = analyticsUsageRoute.useSearch();
  const navigate = analyticsUsageRoute.useNavigate();

  return (
    <AnalyticsScreen
      orgSlug={orgSlug}
      axis="usage"
      days={search.days}
      corpus={search.corpus}
      onRangeChange={(days) =>
        navigate({ search: (prev: UsageSearch) => ({ ...prev, days }) })
      }
      onCorpusChange={(corpus) =>
        navigate({ search: (prev: UsageSearch) => ({ ...prev, corpus }) })
      }
      clock={{
        cycle: search.cycle,
        grain: search.grain,
        term: search.term,
        onCycleChange: (cycle) =>
          navigate({ search: (prev: UsageSearch) => ({ ...prev, cycle }) }),
        onGrainChange: (grain) =>
          navigate({ search: (prev: UsageSearch) => ({ ...prev, grain }) }),
      }}
    />
  );
}
